import { TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Item from './components/Item';
import { getScaleSize, isBetweenRange } from '../../../utils/utils';
import { useNormalizedPixels } from '../../../hooks/useResize';
import PlaceholderItem from './components/Item/PlaceholderItem';
import { BoxComponent } from '../../../base/box/Box';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template8 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, style, development, timezone, scale = 1, tenantId } = props;
  const mainRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const hasBrandLogo = template?.brandLogo && template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  const { normalizePixels } = useNormalizedPixels();

  useFont('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');

  useEffect(() => {
    if (mainRef.current) {
      sizeRef.current = {
        width: mainRef.current.offsetWidth,
        height: mainRef.current.offsetHeight,
      };
      mainRef.current.style.backgroundSize = `${sizeRef.current.width}px ${sizeRef.current.height}px`;
    }
  }, [style?.width, style?.height]);

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }
  };

  const { content, ...rest } = template;

  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!development) {
      const intervalId = setInterval(() => {
        setTick((prev) => prev + 1);
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, [development]);

  // All items from all menus and categories flattened into a single array
  // with schedule checks at menu, category, and item level
  const allItems = useMemo(() => {
    if (!Array.isArray(template?.content)) return [];

    return template.content.flatMap((menu) => {
      // Check menu schedule
      const isMenuActive = development ? true : isBetweenRange(menu.schedules, timezone);
      if (!isMenuActive) return [];

      return (menu.categories || []).flatMap((category) => {
        // Check category schedule
        const isCategoryActive = development ? true : isBetweenRange(category.schedules, timezone);
        if (!isCategoryActive) return [];

        return (category.items || []).filter((item) => {
          // Check item schedule
          const isItemActive = development ? true : isBetweenRange(item.schedules, timezone);
          return isItemActive;
        });
      });
    });
  }, [template?.content, development, timezone, tick]);

  const placeholderItems =
    allItems.length < template.minItemsLimit ? new Array(template.maxItemsLimit - allItems.length).fill(null) : [];

  return (
    <div
      id="template8"
      ref={mainRef}
      style={{
        padding: '3% 0px 6%',
        overflow: 'hidden',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        ...bgStyles(),
        ...(!style ? { height: '100vw', width: '100vh' } : style),
      }}
    >
      <div
        ref={ref}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: getScaleSize(normalizePixels(32, 'height'), scale),
        }}
      >
        {hasBrandLogo && (
          <div style={{ height: getScaleSize(normalizePixels(70, 'height'), scale) }}>
            <img
              src={template?.brandLogo?.image as string}
              alt="Brand logo"
              height={'100%'}
              width={'100%'}
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            //  overflow: template.itemImages?.overflowImages ? 'visible' : 'hidden',
          }}
        >
          {allItems.map((item: any, index: number) => (
            <Item
              key={`row1-${item.id}`}
              item={item}
              template={rest}
              development={development}
              timezone={timezone}
              index={index}
              lastItemIndex={allItems.length - 1}
              scale={scale}
              tenantId={tenantId}
              hasAnyModifier={false}
            />
          ))}
          {placeholderItems.map((_, index) => (
            <PlaceholderItem index={index + allItems.length} template={rest} scale={scale} />
          ))}
        </div>

        {template.taxLabel.isEnabled && (
          <BoxComponent
            sx={{
              position: 'absolute',
              left: '3%',
              bottom: '1%',
            }}
          >
            <TypographyComponent
              sx={{ color: '#000', fontWeight: '500', fontSize: normalizePixels(12, 'height') }}
              variant="subtitle1"
            >
              {template.taxLabel.taxText}
            </TypographyComponent>
          </BoxComponent>
        )}
      </div>
    </div>
  );
});
